/**
 * character/attention.ts — Gaze Attention Engine
 *
 * Computes where a character's eyes should look, how their head should tilt,
 * when they blink, how their hair responds to movement, and how accessories
 * lag behind body motion. This is the system that makes characters feel ALIVE.
 *
 * The engine produces continuous values each frame that get passed to
 * setAttentionContext() before rendering. Every value is physically motivated:
 *
 * - Eyes move with real saccade dynamics (fast snaps + slow drift, not flat lerp)
 * - Head LEADS gaze for large shifts, FOLLOWS for small tracking
 * - Blinks are triggered anticipatorily before gaze shifts (like real humans)
 * - Hair and accessories respond to head velocity, not just body velocity
 * - Torso and shoulders subtly orient toward the attention target
 * - Idle behavior includes comfort shifts, interest glances, and micro-fidgets
 *
 * Priority system (highest wins):
 *   10 — Combat target lock
 *    8 — Conversation partner
 *    6 — Cursor (direct player attention)
 *    3-5 — Nearby entity (distance-scaled)
 *    2 — Nearby creature
 *    0 — Idle wander / saccades
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AttentionTarget {
  type: "device" | "conversation" | "interaction" | "entity" | "cursor" | "idle";
  /** World tile coordinates of the target (for entity/interaction) */
  worldX?: number;
  worldY?: number;
  /** Screen pixel coordinates of the target (for cursor) */
  screenX?: number;
  screenY?: number;
  /** Priority — higher wins. Same-priority targets blend. */
  priority: number;
}

export interface AttentionState {
  // ── Gaze ──
  gazeX: number;          // current interpolated gaze X (-2..2)
  gazeY: number;          // current interpolated gaze Y (-1..1)
  targetGazeX: number;    // where gaze is heading
  targetGazeY: number;
  // ── Head tilt ──
  headTiltX: number;      // current head horizontal shift (-1..1)
  headTiltY: number;      // current head vertical nod (-0.5..0.5)
  // ── Continuous head orientation ──
  headYaw: number;        // head horizontal rotation (-1..1). Wider range than headTiltX.
  headPitch: number;      // head vertical tilt (-0.5..0.5). Wider range than headTiltY.
  // ── Blink ──
  blinkPhase: number;     // 0..1 continuous blink phase
  blinkTimer: number;     // countdown to next blink (seconds)
  blinkDuration: number;  // how long this blink takes (varies for natural feel)
  isBlinking: boolean;    // actively in a blink
  // ── Hair dynamics ──
  hairSwingX: number;     // current horizontal swing offset
  hairVelocityX: number;  // hair velocity for spring simulation
  prevCharVelX: number;   // previous frame's character velocity (for impulse)
  hairSettlePhase: number;
  // ── Secondary motion ──
  secondaryLagX: number;
  secondaryLagY: number;
  secondaryVelX: number;
  secondaryVelY: number;
  // ── Idle saccade ──
  saccadeTimer: number;   // time until next micro-saccade
  saccadeTargetX: number;
  saccadeTargetY: number;

  // ── Saccade dynamics (NEW) ──
  prevTargetX: number;    // last frame's gaze target — detects attention shifts
  prevTargetY: number;
  saccadeSpeed: number;   // current gaze lerp rate (spikes during snaps)
  fixationTimer: number;  // how long we've been fixated on current target
  // ── Anticipatory blink (NEW) ──
  shiftBlinkCooldown: number;  // cooldown before another shift-triggered blink
  // ── Body response (NEW) ──
  shoulderTurnX: number;  // subtle torso/shoulder orientation toward gaze (-1..1)
  breathIntensity: number; // breathing amplitude multiplier (0.6=calm, 1.0=neutral, 1.4=alert)
  // ── Head velocity for hair (NEW) ──
  prevHeadX: number;
  // ── Idle richness (NEW) ──
  comfortTimer: number;    // time until next comfort behavior
  comfortPhase: number;    // 0=none, >0=in comfort anim
  interestLevel: number;   // 0-1 how "engaged" the character is with current target
  // ── Pupil & eyelid (NEW) ──
  pupilDilation: number;   // 0=contracted, 1=normal, 2=dilated. Driven by interest + light
  lidFollowY: number;      // upper eyelid droop from looking down, lift from looking up
  microTremorPhase: number;// continuous physiological nystagmus phase (never stops)
  microTremorX: number;    // current tremor X offset
  microTremorY: number;    // current tremor Y offset
  // ── Eyebrow response (NEW) ──
  browEngagement: number;  // 0=neutral, positive=lifted (interest), negative=furrowed (concentration)
  // ── Movement dynamics (NEW) ──
  arrivalEase: number;     // 0-1 deceleration on last tile of path (1=full speed, 0=stopped)
  // ── Idle micro-fidget (NEW) ──
  fidgetTimer: number;     // countdown to next micro-fidget
  fidgetArmDY: number;     // subtle arm drift from idle fidgets
  fidgetTorsoX: number;    // micro weight shift
  weightShiftPhase: number; // continuous slow weight shift oscillation
}

/** Create a fresh attention state */
export function createAttentionState(): AttentionState {
  return {
    gazeX: 0, gazeY: 0, targetGazeX: 0, targetGazeY: 0,
    headTiltX: 0, headTiltY: 0, headYaw: 0, headPitch: 0,
    blinkPhase: 0, blinkTimer: 2 + Math.random() * 3, blinkDuration: 0.35,
    isBlinking: false,
    hairSwingX: 0, hairVelocityX: 0, prevCharVelX: 0, hairSettlePhase: 0,
    secondaryLagX: 0, secondaryLagY: 0, secondaryVelX: 0, secondaryVelY: 0,
    saccadeTimer: 1 + Math.random() * 2, saccadeTargetX: 0, saccadeTargetY: 0,
    // Saccade dynamics
    prevTargetX: 0, prevTargetY: 0, saccadeSpeed: GAZE_LERP_BASE, fixationTimer: 0,
    // Anticipatory blink
    shiftBlinkCooldown: 0,
    // Body response
    shoulderTurnX: 0, breathIntensity: 1.0,
    // Head velocity
    prevHeadX: 0,
    // Idle richness
    comfortTimer: 5 + Math.random() * 8, comfortPhase: 0, interestLevel: 0,
    // Pupil & eyelid
    pupilDilation: 1.0, lidFollowY: 0,
    microTremorPhase: Math.random() * Math.PI * 2, microTremorX: 0, microTremorY: 0,
    // Eyebrow response
    browEngagement: 0,
    // Movement dynamics
    arrivalEase: 1.0,
    // Idle micro-fidget
    fidgetTimer: 3 + Math.random() * 5, fidgetArmDY: 0, fidgetTorsoX: 0,
    weightShiftPhase: Math.random() * Math.PI * 2,
  };
}

// ─── Constants ──────────────────────────────────────────────────────────────

// Gaze dynamics — saccadic movement
const GAZE_LERP_BASE = 8;        // default smooth tracking rate
const GAZE_LERP_SACCADE = 28;    // fast snap rate for saccadic jumps
const GAZE_LERP_FIXATION = 3;    // slow drift during stable fixation — butter-smooth
const SACCADE_THRESHOLD = 0.8;   // gaze distance that triggers a saccade
const SACCADE_DECAY = 6;         // how fast saccade speed decays — slower = eyes 'settle' naturally

// Head movement
const HEAD_TILT_LERP = 2.5;      // gentle head follow — avoids robotic snap
const HEAD_TILT_RATIO_X = 0.55;  // how much head follows gaze horizontally
const HEAD_TILT_RATIO_Y = 0.4;   // how much head follows gaze vertically
const HEAD_LEAD_THRESHOLD = 1.2; // gaze distance where head LEADS instead of follows
const HEAD_LEAD_LERP = 6;        // faster head movement for anticipatory turns

// Body response
const SHOULDER_TURN_LERP = 2.5;  // slow, organic shoulder rotation
const SHOULDER_TURN_RATIO = 0.25; // fraction of gazeX that becomes shoulder turn
const BREATH_ADAPT_RATE = 0.5;   // how fast breathing adapts to attention intensity

// Hair spring constants (damped harmonic oscillator)
const HAIR_SPRING_K = 45;
const HAIR_DAMPING = 8;
const HAIR_IMPULSE_SCALE = 0.8;
const HAIR_HEAD_IMPULSE = 2.5;   // head turn velocity → hair impulse

// Secondary motion spring (accessories lag more)
const SEC_SPRING_K = 25;
const SEC_DAMPING = 6;
const SEC_IMPULSE_SCALE = 0.5;

// Blink timing
const BLINK_MIN_INTERVAL = 2.0;
const BLINK_MAX_INTERVAL = 6.0;
const BLINK_DURATION_MIN = 0.28;
const BLINK_DURATION_MAX = 0.42;
const SHIFT_BLINK_CHANCE = 0.45;    // probability of blink on attention shift
const SHIFT_BLINK_COOLDOWN = 1.5;   // min seconds between shift-triggered blinks

// Saccade timing (micro eye movements during idle)
const SACCADE_MIN_INTERVAL = 0.8;
const SACCADE_MAX_INTERVAL = 3.0;
const SACCADE_AMPLITUDE = 1.2;

// Idle richness
const COMFORT_MIN_INTERVAL = 6;
const COMFORT_MAX_INTERVAL = 15;
const COMFORT_DURATION = 1.5;

// Pupil dynamics
const PUPIL_ADAPT_RATE = 1.2;    // how fast pupil dilates/contracts
const PUPIL_INTEREST_SCALE = 0.6; // how much interest dilates the pupil

// Micro-tremor (physiological nystagmus) — continuous tiny eye jitter
const TREMOR_FREQ = 2.0;          // Hz — slow organic drift, not rapid oscillation
const TREMOR_AMP_X = 0.06;        // truly sub-pixel — creates life without visible jitter
const TREMOR_AMP_Y = 0.03;        // less vertical than horizontal

// Eyelid vertical tracking
const LID_FOLLOW_RATE = 6;        // how fast lid adjusts to gaze Y
const LID_DOWN_SCALE = 0.35;      // how much lid droops when looking down
const LID_UP_SCALE = -0.15;       // how much lid lifts when looking up (negative = opens)

// Eyebrow engagement response
const BROW_ENGAGE_RATE = 1.5;
const BROW_INTEREST_SCALE = 0.4;  // interest → brow lift
const BROW_CONCENTRATION_SCALE = -0.3; // sustained focus → slight furrow

// Idle micro-fidgets
const FIDGET_MIN_INTERVAL = 4;
const FIDGET_MAX_INTERVAL = 10;
const FIDGET_DECAY = 3.0;         // how fast fidget values decay back to zero
const WEIGHT_SHIFT_SPEED = 0.15;  // continuous slow weight oscillation (radians/sec)

// ─── Helpers ────────────────────────────────────────────────────────────────

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(t, 1);
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

// ─── Main Update ────────────────────────────────────────────────────────────

/**
 * Update the attention state for one frame.
 *
 * @param state   - The mutable attention state to update
 * @param dt      - Delta time in seconds
 * @param targets - Active attention targets (highest priority wins)
 * @param charVelX - Character's horizontal velocity this frame (pixels/sec, screen space)
 * @param charVelY - Character's vertical velocity this frame
 * @param isDeviceOpen - Whether the character is looking at their device
 * @param lookOffset - From AnimationController idle variations (-1=left, 1=right, 2=up)
 * @param weightOffset - From AnimationController idle weight shift
 */
export function updateAttention(
  state: AttentionState,
  dt: number,
  targets: AttentionTarget[],
  charVelX: number,
  charVelY: number,
  isDeviceOpen: boolean,
  lookOffset: number,
  weightOffset: number
): void {
  const safeDt = Math.min(dt, 0.1);

  // ── 1. Determine gaze target ──────────────────────────────────────────

  let newTargetX = 0;
  let newTargetY = 0;
  let targetIntensity = 0; // 0=idle, 1=fully engaged

  if (isDeviceOpen) {
    newTargetX = 0;
    newTargetY = 0.8;
    targetIntensity = 0.7;
  } else if (targets.length > 0) {
    const best = targets.reduce((a, b) => a.priority > b.priority ? a : b);
    newTargetX = clamp(best.screenX ?? 0, -2, 2);
    newTargetY = clamp(best.screenY ?? 0, -1, 1);
    // Higher priority = more engaged
    targetIntensity = clamp(best.priority / 10, 0, 1);
  } else {
    // Idle behavior — layered saccades + comfort shifts
    targetIntensity = 0;

    if (lookOffset === -1) {
      newTargetX = -1.5;
      newTargetY = 0;
    } else if (lookOffset === 1) {
      newTargetX = 1.5;
      newTargetY = 0;
    } else if (lookOffset === 2) {
      newTargetX = 0;
      newTargetY = -0.8;
    } else {
      // Micro-saccades — tiny random eye movements
      state.saccadeTimer -= safeDt;
      if (state.saccadeTimer <= 0) {
        state.saccadeTargetX = (Math.random() - 0.5) * SACCADE_AMPLITUDE;
        state.saccadeTargetY = (Math.random() - 0.5) * SACCADE_AMPLITUDE * 0.5;
        state.saccadeTimer = SACCADE_MIN_INTERVAL + Math.random() * (SACCADE_MAX_INTERVAL - SACCADE_MIN_INTERVAL);
      }

      // Comfort behaviors — occasional larger, purposeful-looking movements
      state.comfortTimer -= safeDt;
      if (state.comfortTimer <= 0 && state.comfortPhase <= 0) {
        state.comfortPhase = COMFORT_DURATION;
        state.comfortTimer = COMFORT_MIN_INTERVAL + Math.random() * (COMFORT_MAX_INTERVAL - COMFORT_MIN_INTERVAL);
      }
      if (state.comfortPhase > 0) {
        state.comfortPhase -= safeDt;
        // Comfort: slow deliberate look in one direction then back
        const ct = 1 - state.comfortPhase / COMFORT_DURATION;
        const comfortX = Math.sin(ct * Math.PI) * 1.8 * (state.saccadeTargetX > 0 ? 1 : -1);
        const comfortY = Math.sin(ct * Math.PI * 0.5) * -0.3; // slight upward glance
        newTargetX = comfortX;
        newTargetY = comfortY;
      } else {
        newTargetX = state.saccadeTargetX;
        newTargetY = state.saccadeTargetY;
      }
    }
  }

  // ── 2. Saccade dynamics — detect attention shifts ─────────────────────

  const shiftDX = newTargetX - state.prevTargetX;
  const shiftDY = newTargetY - state.prevTargetY;
  const shiftDist = Math.sqrt(shiftDX * shiftDX + shiftDY * shiftDY);

  if (shiftDist > SACCADE_THRESHOLD) {
    // Large gaze shift — trigger saccadic snap
    state.saccadeSpeed = GAZE_LERP_SACCADE;
    state.fixationTimer = 0;

    // Anticipatory blink — humans often blink right before or during a gaze shift
    state.shiftBlinkCooldown -= safeDt;
    if (state.shiftBlinkCooldown <= 0 && !state.isBlinking && Math.random() < SHIFT_BLINK_CHANCE) {
      state.isBlinking = true;
      state.blinkPhase = 0;
      state.blinkDuration = BLINK_DURATION_MIN; // quick shift blink
      state.shiftBlinkCooldown = SHIFT_BLINK_COOLDOWN;
    }
  } else {
    // Smooth tracking — decay saccade speed toward fixation rate
    state.saccadeSpeed = lerp(state.saccadeSpeed, GAZE_LERP_FIXATION, SACCADE_DECAY * safeDt);
    state.fixationTimer += safeDt;
  }
  state.shiftBlinkCooldown -= safeDt;

  state.targetGazeX = newTargetX;
  state.targetGazeY = newTargetY;
  state.prevTargetX = newTargetX;
  state.prevTargetY = newTargetY;

  // ── 3. Interpolate gaze with saccade dynamics ────────────────────────

  const gazeRate = state.saccadeSpeed * safeDt;
  state.gazeX = lerp(state.gazeX, state.targetGazeX, gazeRate);
  state.gazeY = lerp(state.gazeY, state.targetGazeY, gazeRate);

  // ── 4. Head tilt — asymmetric lead/follow ───���────────────────────────
  // For small movements: head FOLLOWS gaze (eyes arrive first, head catches up)
  // For large movements: head LEADS gaze (anticipatory turn)

  const gazeDist = Math.sqrt(
    (state.targetGazeX - state.gazeX) ** 2 +
    (state.targetGazeY - state.gazeY) ** 2
  );
  const headRate = gazeDist > HEAD_LEAD_THRESHOLD
    ? HEAD_LEAD_LERP * safeDt    // large shift: head leads
    : HEAD_TILT_LERP * safeDt;   // small tracking: head follows

  const headTargetX = state.targetGazeX * HEAD_TILT_RATIO_X + weightOffset * 0.3;
  const headTargetY = state.targetGazeY * HEAD_TILT_RATIO_Y;

  // Track head velocity for hair impulse
  const prevHTX = state.headTiltX;
  state.headTiltX = lerp(state.headTiltX, headTargetX, headRate);
  state.headTiltY = lerp(state.headTiltY, headTargetY, headRate);

  // Continuous head yaw/pitch — wider range than headTiltX/Y, drives head shape
  // deformation and face feature placement for 72+ apparent orientations.
  // Yaw: -1 (full left) to +1 (full right), tracks gaze with heavier damping
  const yawTarget = clamp(state.targetGazeX * 0.5, -1, 1);
  const pitchTarget = clamp(state.targetGazeY * 0.5, -0.5, 0.5);
  const yawRate = headRate * 0.8; // slightly slower than tilt for weight
  state.headYaw = lerp(state.headYaw, yawTarget, yawRate);
  state.headPitch = lerp(state.headPitch, pitchTarget, yawRate);

  // Head turn velocity → hair impulse (head snaps = hair swings)
  const headVelX = state.headTiltX - prevHTX;
  state.prevHeadX = headVelX;

  // ── 5. Interest level — smooth engagement tracking ───────────────────

  state.interestLevel = lerp(state.interestLevel, targetIntensity, 1.5 * safeDt);

  // ── 6. Body response — shoulder turn + breathing ─────────────────────

  // Shoulders subtly orient toward gaze direction
  const shoulderTarget = state.gazeX * SHOULDER_TURN_RATIO * (1 + state.interestLevel * 0.5);
  state.shoulderTurnX = lerp(state.shoulderTurnX, shoulderTarget, SHOULDER_TURN_LERP * safeDt);

  // Breathing adapts: calm during idle, heightened during engagement
  const targetBreath = 0.7 + state.interestLevel * 0.6; // 0.7 (calm) → 1.3 (alert)
  state.breathIntensity = lerp(state.breathIntensity, targetBreath, BREATH_ADAPT_RATE * safeDt);

  // ── 7. Blink simulation ───────────────────────────────────────────────

  if (!state.isBlinking) {
    state.blinkTimer -= safeDt;
    if (state.blinkTimer <= 0) {
      state.isBlinking = true;
      state.blinkPhase = 0;
      state.blinkDuration = BLINK_DURATION_MIN + Math.random() * (BLINK_DURATION_MAX - BLINK_DURATION_MIN);
    }
    state.blinkPhase = 0;
  } else {
    state.blinkPhase += safeDt / state.blinkDuration;
    if (state.blinkPhase >= 1.0) {
      state.isBlinking = false;
      state.blinkPhase = 0;
      state.blinkTimer = BLINK_MIN_INTERVAL + Math.random() * (BLINK_MAX_INTERVAL - BLINK_MIN_INTERVAL);
      // More frequent blinks when engaged (attention fatigue)
      if (state.interestLevel > 0.5) {
        state.blinkTimer *= 0.7;
      }
    }
  }

  // ── 8. Hair dynamics — damped spring with head turn impulse ───────────

  const velDelta = charVelX - state.prevCharVelX;
  state.prevCharVelX = charVelX;

  // Impulse from body velocity change (acceleration = visual "snap")
  state.hairVelocityX -= velDelta * HAIR_IMPULSE_SCALE;
  // Constant drag from steady movement (hair streams behind)
  state.hairVelocityX -= charVelX * 0.02;
  // Head turn impulse — quick head snaps make hair swing (new!)
  state.hairVelocityX -= headVelX * HAIR_HEAD_IMPULSE / Math.max(safeDt, 0.001);

  // Spring force: pulls hair back to center
  const springForce = -HAIR_SPRING_K * state.hairSwingX;
  const dampingForce = -HAIR_DAMPING * state.hairVelocityX;

  state.hairVelocityX += (springForce + dampingForce) * safeDt;
  state.hairSwingX += state.hairVelocityX * safeDt;
  state.hairSwingX = clamp(state.hairSwingX, -3, 3);

  state.hairSettlePhase = Math.abs(state.hairSwingX) > 0.1 ? 1 : Math.max(0, state.hairSettlePhase - safeDt * 2);

  // ── 9. Secondary motion — accessory lag spring ────────────────────────

  const secImpulseX = -charVelX * SEC_IMPULSE_SCALE;
  const secImpulseY = -charVelY * SEC_IMPULSE_SCALE;

  state.secondaryVelX += (-SEC_SPRING_K * state.secondaryLagX - SEC_DAMPING * state.secondaryVelX + secImpulseX) * safeDt;
  state.secondaryVelY += (-SEC_SPRING_K * state.secondaryLagY - SEC_DAMPING * state.secondaryVelY + secImpulseY) * safeDt;

  state.secondaryLagX += state.secondaryVelX * safeDt;
  state.secondaryLagY += state.secondaryVelY * safeDt;
  state.secondaryLagX = clamp(state.secondaryLagX, -2, 2);
  state.secondaryLagY = clamp(state.secondaryLagY, -1, 1);

  // ── 10. Physiological micro-tremor — eyes are NEVER perfectly still ──
  // Real human eyes exhibit constant involuntary micro-movements (nystagmus).
  // This is what separates "alive" from "painted." Uses two incommensurate
  // frequencies to avoid visible periodicity.
  state.microTremorPhase += TREMOR_FREQ * Math.PI * 2 * safeDt;
  if (state.microTremorPhase > Math.PI * 200) state.microTremorPhase -= Math.PI * 200;
  state.microTremorX = Math.sin(state.microTremorPhase) * TREMOR_AMP_X
    + Math.sin(state.microTremorPhase * 1.73) * TREMOR_AMP_X * 0.5; // second harmonic
  state.microTremorY = Math.cos(state.microTremorPhase * 0.87) * TREMOR_AMP_Y
    + Math.sin(state.microTremorPhase * 2.31) * TREMOR_AMP_Y * 0.3;
  // Tremor dampens during saccades (eyes are ballistic during fast snaps)
  if (state.saccadeSpeed > GAZE_LERP_BASE * 1.5) {
    state.microTremorX *= 0.2;
    state.microTremorY *= 0.2;
  }

  // ── 11. Pupil dilation — driven by interest, not just expression ──
  // When you're interested in something, your pupils dilate. When bored or
  // fatigued, they contract. This is an autonomic response that viewers
  // perceive subconsciously — it makes characters feel emotionally present.
  const targetDilation = 1.0 + state.interestLevel * PUPIL_INTEREST_SCALE;
  state.pupilDilation = lerp(state.pupilDilation, targetDilation, PUPIL_ADAPT_RATE * safeDt);

  // ── 12. Eyelid follows gaze vertically — the biggest "alive" tell ──
  // When looking down, the upper eyelid partially closes (it follows the iris).
  // When looking up, the lid lifts. This is involuntary and universal.
  const gazeYNorm = state.gazeY; // -1 (up) to +1 (down)
  const targetLid = gazeYNorm > 0
    ? gazeYNorm * LID_DOWN_SCALE   // looking down → lid droops
    : gazeYNorm * LID_UP_SCALE;    // looking up → lid lifts (negative → opens)
  state.lidFollowY = lerp(state.lidFollowY, targetLid, LID_FOLLOW_RATE * safeDt);

  // ── 13. Eyebrow engagement response ──
  // Brows lift slightly with interest/surprise, furrow during sustained concentration.
  // This happens independently of expression — it's the attention system's contribution.
  let browTarget = 0;
  if (state.interestLevel > 0.6) {
    // High engagement: slight lift (curiosity, surprise)
    browTarget = (state.interestLevel - 0.6) * BROW_INTEREST_SCALE / 0.4;
  }
  if (state.fixationTimer > 3.0) {
    // Sustained focus: gradual furrow (concentration)
    const concentrationT = Math.min((state.fixationTimer - 3.0) / 5.0, 1.0);
    browTarget += concentrationT * BROW_CONCENTRATION_SCALE;
  }
  state.browEngagement = lerp(state.browEngagement, browTarget, BROW_ENGAGE_RATE * safeDt);

  // ── 14. Idle micro-fidgets — continuous living motion ──
  // Real people are never perfectly still. Weight shifts, arm adjustments,
  // micro-postural corrections happen constantly. These are faster and subtler
  // than the comfort behaviors in section 1.
  const isIdle = targetIntensity < 0.2 && Math.abs(charVelX) < 1 && Math.abs(charVelY) < 1;
  if (isIdle) {
    state.fidgetTimer -= safeDt;
    if (state.fidgetTimer <= 0) {
      // Trigger a random micro-fidget
      state.fidgetArmDY = (Math.random() - 0.5) * 1.5;  // subtle arm drift
      state.fidgetTorsoX = (Math.random() - 0.5) * 0.4;  // micro weight shift
      state.fidgetTimer = FIDGET_MIN_INTERVAL + Math.random() * (FIDGET_MAX_INTERVAL - FIDGET_MIN_INTERVAL);
    }
    // Continuous slow weight oscillation — breathing weight transfer
    state.weightShiftPhase += WEIGHT_SHIFT_SPEED * safeDt;
    if (state.weightShiftPhase > Math.PI * 2) state.weightShiftPhase -= Math.PI * 2;
  }
  // Decay fidget values smoothly back to zero
  state.fidgetArmDY = lerp(state.fidgetArmDY, 0, FIDGET_DECAY * safeDt);
  state.fidgetTorsoX = lerp(state.fidgetTorsoX, 0, FIDGET_DECAY * safeDt);

  // ── 15. Movement arrival deceleration ──
  // When moving, arrivalEase stays at 1.0. The render system uses this to
  // ease-out the last tile of movement for a natural stop. Reset here each frame;
  // the engine sets it based on queue state.
  // (This value is set externally by the movement system — we just smooth it)
}

/**
 * Compute gaze target from a world-space entity position relative to the character.
 * Returns screen-space normalized gaze offsets suitable for AttentionTarget.
 */
export function worldToGaze(
  charTileX: number, charTileY: number,
  targetTileX: number, targetTileY: number,
  charDir: string
): { screenX: number; screenY: number } {
  const dx = targetTileX - charTileX;
  const dy = targetTileY - charTileY;
  const sx = (dx - dy);
  const sy = (dx + dy) * 0.5;

  const dist = Math.sqrt(sx * sx + sy * sy) || 1;
  const maxGazeDist = 5;
  const t = Math.min(dist / maxGazeDist, 1);

  return {
    screenX: clamp((sx / dist) * t * 2, -2, 2),
    screenY: clamp((sy / dist) * t * 1, -1, 1),
  };
}

/**
 * Compute gaze target from cursor screen position relative to character screen position.
 */
export function cursorToGaze(
  charScreenX: number, charScreenY: number,
  cursorX: number, cursorY: number,
  eyeOffset: number = -30
): { screenX: number; screenY: number } {
  const dx = cursorX - charScreenX;
  const dy = cursorY - (charScreenY + eyeOffset);
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const maxDist = 200;
  const t = Math.min(dist / maxDist, 1);

  return {
    screenX: clamp((dx / dist) * t * 2, -2, 2),
    screenY: clamp((dy / dist) * t * 0.8, -1, 1),
  };
}
